import { useMemo } from 'react';
import type {
  BriefingHistoryListData,
  BriefingHistoryListItem,
} from '../../hooks/useBriefingHistory';
import { formatBriefingCalendarDate } from '../../lib/formatting';

export interface BriefingBucket {
  key: 'recent' | 'older';
  label: string;
  items: BriefingHistoryListItem[];
}

// Splits date-DESC briefings into "Last 7 days" and "Older" by briefing_date (a
// 'YYYY-MM-DD' calendar string). `now` is injected so the split is pure and
// unit-testable. The window is inclusive of now's date back through 7 days prior;
// string comparison is sound because both sides are zero-padded ISO dates. Input
// order is preserved within each bucket (the BFF returns briefing_date DESC), and
// empty buckets are dropped so a header never renders over nothing.
export function bucketByRecency(
  briefings: BriefingHistoryListItem[],
  now: Date,
): BriefingBucket[] {
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
  const cutoffYmd = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;

  const recent: BriefingHistoryListItem[] = [];
  const older: BriefingHistoryListItem[] = [];
  for (const b of briefings) {
    if (b.briefing_date >= cutoffYmd) recent.push(b);
    else older.push(b);
  }

  const buckets: BriefingBucket[] = [];
  if (recent.length) buckets.push({ key: 'recent', label: 'LAST 7 DAYS', items: recent });
  if (older.length) buckets.push({ key: 'older', label: 'OLDER', items: older });
  return buckets;
}

function HistoryRow({
  item,
  showTeam,
  onNav,
}: {
  item: BriefingHistoryListItem;
  showTeam: boolean;
  onNav: (id: string) => void;
}) {
  return (
    <button className="hist-row" onClick={() => onNav(`briefing-history/${item.id}`)}>
      <span className="hist-date">{formatBriefingCalendarDate(item.briefing_date)}</span>
      <span className="hist-preview">{item.preview ?? 'View briefing'}</span>
      {showTeam && item.team_name && <span className="hist-team">{item.team_name}</span>}
      {!item.viewed_at && <span className="hist-new">new</span>}
    </button>
  );
}

export function BriefingHistorySurface({
  data,
  onNav,
}: {
  data: BriefingHistoryListData;
  onNav: (id: string) => void;
}) {
  const briefings = data.briefings ?? [];
  const buckets = useMemo(() => bucketByRecency(briefings, new Date()), [briefings]);
  // Only label rows with their team when the history spans more than one team —
  // pre-launch the common case is a single team, where a chip would be noise.
  const showTeam = useMemo(
    () => new Set(briefings.map((b) => b.team_id)).size > 1,
    [briefings],
  );

  return (
    <div className="v2-content ov-surface">
      <div className="wrap">
        <div className="crumb">DELIVER · ARCHIVE</div>
        <h1 className="greeting">
          Past <span className="em">briefings</span>
        </h1>
        <p className="sub">Your daily briefings, archived as they were generated.</p>

        {briefings.length === 0 ? (
          <div className="card" style={{ marginTop: 26, padding: '44px 28px', textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: 'var(--inkS)', lineHeight: 1.6, margin: 0 }}>
              No past briefings yet. Daily briefings appear here once they start
              generating for your team.
            </p>
          </div>
        ) : (
          buckets.map((bucket) => (
            <div className="sec-block" key={bucket.key}>
              <div className="sec-label">
                {bucket.key === 'recent' ? (
                  <span className="kick acc">
                    <span className="d" />
                    {bucket.label}
                  </span>
                ) : (
                  <span className="kick" style={{ color: 'var(--faint)' }}>
                    <span className="d" style={{ background: 'var(--faint)' }} />
                    {bucket.label}
                  </span>
                )}
                <span className="ct">{bucket.items.length}</span>
              </div>
              <div className="card" style={{ overflow: 'hidden' }}>
                {bucket.items.map((item) => (
                  <HistoryRow key={item.id} item={item} showTeam={showTeam} onNav={onNav} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
